import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import type { PageDto } from '@/lib/pagination';
import { useAuth } from '@/lib/auth/AuthContext';
import type { UploadedDocumentOut } from '../types';

export function useAssignUploadedDocumentToPatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId, variables.documentId] });
    },
    mutationFn: async ({ documentId, patient_id }: { documentId: string; patient_id: string }) => {
      const { data } = await clientApi.post<UploadedDocumentOut>(
        `${orgUrl(orgId!)}/uploaded-documents/${documentId}/assign`,
        { patient_id },
      );
      return data;
    },
  });
}

// ─── Queries ────────────────────────────────────────────────────

export function useCreateRevision() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId, variables.documentId] });
    },
    mutationFn: async ({
      file,
      documentId,
      extracted_text,
    }: {
      documentId: string;
      file?: File;
      extracted_text?: string;
    }) => {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      if (extracted_text !== undefined) {
        formData.append('extracted_text', extracted_text);
      }

      const { data } = await clientApi.post<UploadedDocumentOut>(
        `${orgUrl(orgId!)}/uploaded-documents/${documentId}/revisions`,
        formData,
        { timeout: 300000, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
  });
}

export function useDeleteUploadedDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId] });
    },
    mutationFn: async (documentId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/uploaded-documents/${documentId}`);
    },
  });
}

export function useDocumentDownloadUrl(documentId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: false, // only fetch on demand
    queryKey: ['uploaded-documents', orgId, documentId, 'download'],
    queryFn: async () => {
      const { data } = await clientApi.get<{ url: string; file_name: string; content_type: string }>(
        `${orgUrl(orgId!)}/uploaded-documents/${documentId}/download`,
      );
      return data;
    },
  });
}

// ─── Mutations ──────────────────────────────────────────────────

export function useLinkEncounter() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId, variables.documentId] });
    },
    mutationFn: async ({ documentId, encounterId }: { documentId: string; encounterId: string }) => {
      await clientApi.post(`${orgUrl(orgId!)}/uploaded-documents/${documentId}/encounters/${encounterId}`);
    },
  });
}

export function useTriggerExtraction() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId, documentId] });
    },
    mutationFn: async (documentId: string) => {
      await clientApi.post(`${orgUrl(orgId!)}/uploaded-documents/${documentId}/extract`);
    },
  });
}

export function useUnlinkEncounter() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId, variables.documentId] });
    },
    mutationFn: async ({ documentId, encounterId }: { documentId: string; encounterId: string }) => {
      await clientApi.delete(`${orgUrl(orgId!)}/uploaded-documents/${documentId}/encounters/${encounterId}`);
    },
  });
}

export function useUpdateUploadedDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId, variables.documentId] });
    },
    mutationFn: async ({
      data,
      documentId,
    }: {
      documentId: string;
      data: {
        name?: string;
        document_type?: string;
        document_date?: string;
        notes?: string | null;
      };
    }) => {
      const { data: result } = await clientApi.patch<UploadedDocumentOut>(
        `${orgUrl(orgId!)}/uploaded-documents/${documentId}`,
        data,
      );
      return result;
    },
  });
}

export function useUploadDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-documents', orgId] });
    },
    mutationFn: async ({
      file,
      name,
      notes,
      patient_id,
      document_type,
      document_date,
    }: {
      file: File;
      name: string;
      document_type: string;
      document_date: string;
      notes?: string;
      /**
       * Optional since Plan 06: omit (or pass null) for an Inbox upload that
       * gets adjudicated to a patient later via {@link useAssignUploadedDocumentToPatient}.
       */
      patient_id?: string | null;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'metadata',
        JSON.stringify({
          name,
          document_type,
          document_date,
          ...(patient_id ? { patient_id } : {}),
          ...(notes ? { notes } : {}),
        }),
      );

      const { data } = await clientApi.post<UploadedDocumentOut>(`${orgUrl(orgId!)}/uploaded-documents`, formData, {
        timeout: 300000,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}

export function useUploadedDocument(documentId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!documentId,
    queryKey: ['uploaded-documents', orgId, documentId],
    queryFn: async () => {
      const { data } = await clientApi.get<UploadedDocumentOut>(`${orgUrl(orgId!)}/uploaded-documents/${documentId}`);
      return data;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.current_revision?.extraction_status;
      return status === 'pending' || status === 'extracting' ? 5000 : false;
    },
  });
}

export function useUploadedDocuments(params?: {
  source_provider?: 'srfax';
  limit?: number;
  offset?: number;
  patient_id?: string;
  /** When true, only return orphan (Inbox) uploads — patient_id IS NULL. */
  inbox?: boolean;
}, enabled = true) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['uploaded-documents', orgId, params],
    refetchInterval: params?.inbox ? 30000 : false,
    // Inbox callers omit ``patient_id`` but pass ``inbox: true`` — accept either.
    enabled: !!orgId && enabled && (!!params?.patient_id || !!params?.inbox),
    queryFn: async () => {
      const { data } = await clientApi.get<PageDto<UploadedDocumentOut>>(`${orgUrl(orgId!)}/uploaded-documents`, {
        params,
      });
      return data;
    },
  });
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}`;
}
